import React, {useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import * as THREE from "three";

/**
 * Creates a soft procedural bokeh particle texture (circular radiant gradient)
 * without requiring any external network asset.
 */
const createBokehTexture = () => {
    if (typeof document === "undefined") return null;
    try {
        const canvas = document.createElement("canvas");
        canvas.width = 64;
        canvas.height = 64;
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;

        const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
        gradient.addColorStop(0, "rgba(255, 255, 255, 1.0)");
        gradient.addColorStop(0.25, "rgba(238, 224, 192, 0.85)");
        gradient.addColorStop(0.55, "rgba(143, 179, 156, 0.4)");
        gradient.addColorStop(1, "rgba(40, 82, 56, 0.0)");

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 64, 64);
        return new THREE.CanvasTexture(canvas);
    } catch {
        return null;
    }
};

/**
 * Den Resonante Pustekulen (The Calming Breathing Orb)
 * with Skogsro & Svevestøv (Nordic Light Spores / Bokeh Particles)
 *
 * A subtle, therapeutic 3D breathing sphere surrounded by drifting light spores.
 * Expands and contracts gently along a 4s inhale / 4s exhale therapeutic rhythm.
 *
 * Accessibility & Performance:
 * - WCAG 2.3.3: Respects prefers-reduced-motion (renders static soft sphere without RAF loop).
 * - WCAG 2.2.2: Provides a visible Pause/Play button so users can stop motion.
 * - IntersectionObserver: Pauses animation loop when scrolled out of view.
 * - Memory leak free: Complete disposal of geometries, materials, textures and WebGL context on unmount.
 */
export const BreathingOrb = ({
    size = 230,
    showControls = true,
    showLabel = true,
    showSpores = true,
    sx = {},
}) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const [isPaused, setIsPaused] = useState(false);
    const isPausedRef = useRef(false);
    const [breathPhase, setBreathPhase] = useState("Pust inn... (4s)");
    const [reducedMotion, setReducedMotion] = useState(() => {
        if (typeof window !== "undefined" && window.matchMedia) {
            return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        }
        return false;
    });

    // Synchronize pause state into ref without triggering effect re-creation
    useEffect(() => {
        isPausedRef.current = isPaused;
    }, [isPaused]);

    // Detect prefers-reduced-motion changes
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        const handleChange = (e) => setReducedMotion(e.matches);
        mediaQuery.addEventListener?.("change", handleChange);
        return () => mediaQuery.removeEventListener?.("change", handleChange);
    }, []);

    useEffect(() => {
        if (reducedMotion || !containerRef.current || !canvasRef.current) return;

        let scene, camera, renderer, sphereMesh, innerGlowMesh;
        let particleGeo, particleMat, bokehTexture, particlePoints;
        let animationFrameId = null;
        let isVisible = true;

        // Check WebGL availability safely
        try {
            renderer = new THREE.WebGLRenderer({
                canvas: canvasRef.current,
                alpha: true,
                antialias: true,
                powerPreference: "low-power",
            });
        } catch (e) {
            console.warn("WebGL not supported, falling back to CSS orb.", e);
            return;
        }

        const width = size;
        const height = size;

        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));

        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
        camera.position.z = 4.4;

        // Lighting: soft ambient + warm morning sunlight + sage rim light
        const ambientLight = new THREE.AmbientLight(0xfaf8f5, 1.3);
        scene.add(ambientLight);

        const mainLight = new THREE.DirectionalLight(0xe8ede9, 1.5);
        mainLight.position.set(3, 4, 3);
        scene.add(mainLight);

        const warmLight = new THREE.PointLight(0xd97746, 0.45, 10);
        warmLight.position.set(-2, -3, 1);
        scene.add(warmLight);

        // Outer organic breathing sphere
        const geometry = new THREE.IcosahedronGeometry(1.15, 16);
        const originalPositions = geometry.attributes.position.clone();

        const material = new THREE.MeshPhysicalMaterial({
            color: 0x417554,
            roughness: 0.25,
            metalness: 0.05,
            transmission: 0.75,
            ior: 1.2,
            transparent: true,
            opacity: 0.88,
        });

        sphereMesh = new THREE.Mesh(geometry, material);
        scene.add(sphereMesh);

        // Inner glowing core
        const innerGeo = new THREE.SphereGeometry(0.72, 24, 24);
        const innerMat = new THREE.MeshBasicMaterial({
            color: 0x8fb39c,
            transparent: true,
            opacity: 0.45,
        });
        innerGlowMesh = new THREE.Mesh(innerGeo, innerMat);
        scene.add(innerGlowMesh);

        // Skogsro & Svevestøv (Nordic Light Spores / Bokeh Particles)
        const particleCount = showSpores ? 42 : 0;
        let particlePositions = null;
        let particleSpeeds = null;
        let particleOffsets = null;

        if (particleCount > 0) {
            bokehTexture = createBokehTexture();
            particleGeo = new THREE.BufferGeometry();
            particlePositions = new Float32Array(particleCount * 3);
            particleSpeeds = new Float32Array(particleCount);
            particleOffsets = new Float32Array(particleCount);

            for (let i = 0; i < particleCount; i++) {
                particlePositions[i * 3 + 0] = (Math.random() - 0.5) * 4.6;
                particlePositions[i * 3 + 1] = (Math.random() - 0.5) * 4.6;
                particlePositions[i * 3 + 2] = (Math.random() - 0.5) * 2.8;
                particleSpeeds[i] = 0.12 + Math.random() * 0.22;
                particleOffsets[i] = Math.random() * Math.PI * 2;
            }

            particleGeo.setAttribute(
                "position",
                new THREE.BufferAttribute(particlePositions, 3)
            );

            particleMat = new THREE.PointsMaterial({
                size: 0.28,
                map: bokehTexture || void 0,
                transparent: true,
                opacity: 0.6,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
            });

            particlePoints = new THREE.Points(particleGeo, particleMat);
            scene.add(particlePoints);
        }

        // Breathing rhythm variables (8.0s cycle: 4.0s in, 4.0s out)
        const CYCLE_DURATION = 8.0;
        let accumulatedTime = 0;
        let lastFrameTime = performance.now();
        let currentPhaseText = "";

        const animate = () => {
            animationFrameId = requestAnimationFrame(animate);

            if (!isVisible) return;

            // When paused, maintain the static rendered scene without advancing time
            if (isPausedRef.current) {
                lastFrameTime = performance.now();
                renderer.render(scene, camera);
                return;
            }

            const now = performance.now();
            const delta = Math.min((now - lastFrameTime) / 1000, 0.1);
            lastFrameTime = now;
            accumulatedTime += delta;

            const cycleTime = accumulatedTime % CYCLE_DURATION;
            const cycleProgress = cycleTime / CYCLE_DURATION;
            const isInhaling = cycleTime < 4.0;

            // Seconds countdown within current phase (4s, 3s, 2s, 1s)
            const phaseElapsed = isInhaling ? cycleTime : cycleTime - 4.0;
            const secondsRemaining = Math.max(1, 4 - Math.floor(phaseElapsed));

            // Sync phase text: "Pust inn... (4s)" or "Pust ut... (4s)"
            const nextPhaseText = `${isInhaling ? "Pust inn..." : "Pust ut..."} (${secondsRemaining}s)`;
            if (nextPhaseText !== currentPhaseText) {
                currentPhaseText = nextPhaseText;
                setBreathPhase(nextPhaseText);
            }

            // Monotonic respiration curve:
            // At cycleTime = 0.0s (start of inhale): -cos(0) = -1.0 (minimum scale, empty lungs)
            // Monotonically expands throughout the 4.0s inhale until peak (+1.0) at 4.0s
            // At cycleTime = 4.0s (start of exhale): -cos(pi) = +1.0 (maximum scale, full lungs)
            // Monotonically contracts throughout the 4.0s exhale until minimum (-1.0) at 8.0s
            const respiration = -Math.cos(cycleProgress * Math.PI * 2);
            const scale = 1.0 + respiration * 0.16;
            sphereMesh.scale.set(scale, scale, scale);
            innerGlowMesh.scale.set(scale * 0.95, scale * 0.95, scale * 0.95);

            // Subtle rotation
            sphereMesh.rotation.y = accumulatedTime * 0.12;
            sphereMesh.rotation.x = Math.sin(accumulatedTime * 0.08) * 0.1;

            // Delicate organic surface wave
            const pos = geometry.attributes.position;
            const orig = originalPositions;
            for (let i = 0; i < pos.count; i++) {
                const u = orig.getX(i);
                const v = orig.getY(i);
                const w = orig.getZ(i);

                const wave =
                    Math.sin(u * 2.5 + accumulatedTime * 1.2) *
                    Math.cos(v * 2.5 + accumulatedTime * 1.0) *
                    0.035;

                pos.setXYZ(i, u + u * wave, v + v * wave, w + w * wave);
            }
            pos.needsUpdate = true;

            // Update Skogsro & Svevestøv light spores
            if (particlePoints && particleGeo) {
                const pArray = particleGeo.attributes.position.array;
                for (let i = 0; i < particleCount; i++) {
                    const idx = i * 3;
                    pArray[idx + 1] += particleSpeeds[i] * 0.008;
                    pArray[idx + 0] +=
                        Math.sin(accumulatedTime * 0.7 + particleOffsets[i]) * 0.002;

                    // Wrap around top boundary back to bottom
                    if (pArray[idx + 1] > 2.4) {
                        pArray[idx + 1] = -2.4;
                        pArray[idx + 0] = (Math.random() - 0.5) * 4.6;
                        pArray[idx + 2] = (Math.random() - 0.5) * 2.8;
                    }
                }
                particleGeo.attributes.position.needsUpdate = true;

                // Subtle spore breathing pulse opacity
                if (particleMat) {
                    particleMat.opacity = 0.45 + (respiration + 1) * 0.12;
                }
            }

            renderer.render(scene, camera);
        };

        // Pause when out of viewport using IntersectionObserver (if available)
        let observer = null;
        if (typeof IntersectionObserver !== "undefined") {
            observer = new IntersectionObserver(
                ([entry]) => {
                    isVisible = entry.isIntersecting;
                },
                { threshold: 0.1 }
            );

            if (containerRef.current) {
                observer.observe(containerRef.current);
            }
        }

        // Handle tab visibility change
        const handleVisibilityChange = () => {
            isVisible = document.visibilityState === "visible";
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Start animation loop
        animate();

        // Cleanup
        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            if (observer) {
                observer.disconnect();
            }

            // Explicit WebGL resource disposal to prevent memory leaks
            geometry.dispose();
            originalPositions.dispose?.();
            material.dispose();
            innerGeo.dispose();
            innerMat.dispose();
            if (particleGeo) particleGeo.dispose();
            if (particleMat) particleMat.dispose();
            if (bokehTexture) bokehTexture.dispose();
            if (renderer) {
                renderer.dispose();
                renderer.forceContextLoss?.();
            }
        };
    }, [size, reducedMotion, showSpores]);

    return (
        <Box
            ref={containerRef}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                width: size,
                maxWidth: "100%",
                mx: "auto",
                my: 1,
                userSelect: "none",
                ...sx,
            }}
        >
            {/* Reduced motion static fallback */}
            {reducedMotion ? (
                <Box
                    aria-hidden="true"
                    sx={{
                        width: size * 0.85,
                        height: size * 0.85,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle at 35% 35%, rgba(143, 179, 156, 0.9) 0%, rgba(65, 117, 84, 0.85) 60%, rgba(40, 82, 56, 0.9) 100%)",
                        boxShadow: "0 8px 32px rgba(40, 82, 56, 0.2)",
                        border: "2px solid rgba(255, 255, 255, 0.4)",
                    }}
                />
            ) : (
                <canvas
                    ref={canvasRef}
                    aria-hidden="true"
                    style={{
                        width: size,
                        height: size,
                        display: "block",
                        pointerEvents: "none",
                    }}
                />
            )}

            {/* Subtitle & Breathing Guide Indicator */}
            {showLabel && (
                <Box sx={{textAlign: "center", mt: 1}} aria-live="polite">
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            color: "primary.main",
                            fontSize: "0.78rem",
                            display: "block",
                        }}
                    >
                        {reducedMotion ? "Rolig pust" : isPaused ? "Pust (pauset)" : breathPhase}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{fontSize: "0.72rem", fontStyle: "italic"}}
                    >
                        4s innånding · 4s utånding
                    </Typography>
                </Box>
            )}

            {/* Accessible Motion Pause / Play Control (WCAG 2.2.2) */}
            {showControls && !reducedMotion && (
                <Tooltip title={isPaused ? "Fortsett pusteanimasjon" : "Paus animasjon"}>
                    <IconButton
                        size="small"
                        onClick={() => setIsPaused((prev) => !prev)}
                        aria-label={isPaused ? "Start pusteanimasjon" : "Paus pusteanimasjon"}
                        sx={{
                            position: "absolute",
                            bottom: showLabel ? 32 : 4,
                            right: 4,
                            bgcolor: "rgba(255, 255, 255, 0.85)",
                            backdropFilter: "blur(4px)",
                            border: "1px solid rgba(40, 82, 56, 0.18)",
                            width: 36,
                            height: 36,
                            minWidth: 36,
                            minHeight: 36,
                            "&:hover": {bgcolor: "rgba(255, 255, 255, 0.98)"},
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: 2,
                            },
                        }}
                    >
                        {isPaused ? (
                            <PlayArrowIcon sx={{fontSize: 18, color: "primary.main"}} />
                        ) : (
                            <PauseIcon sx={{fontSize: 18, color: "primary.main"}} />
                        )}
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};
